import React, { memo, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Edit3, Trash2, Link2, Monitor } from 'lucide-react';
import { televisaoService, Televisao } from '../services/televisaoService';
import { TelaMinima } from '../../banners/services/bannerService';

interface TelevisaoCardProps {
    televisao: Televisao;
    telasVinculadas: TelaMinima[]; // Apenas id e nome para busca inicial
    onEdit: (televisao: Televisao) => void;
    onDelete: (id: number | null) => void;
    onLinkScreens: (televisao: Televisao) => void;
}

export const TelevisaoCard = memo(({
    televisao,
    telasVinculadas,
    onEdit,
    onDelete,
    onLinkScreens
}: TelevisaoCardProps) => {
    const navigate = useNavigate();

    const telaIds = televisaoService.stringToTelaIds(televisao?.dsTelasCadastradas);
    const quantidadeTelas = telaIds.length;
    
    // Ordenar telas pela ordem do dsConfiguracoes
    const telasOrdenadas = useMemo(() => {
        if (quantidadeTelas === 0) return [];
        
        const configs = televisaoService.extrairTodasConfigs(televisao?.dsConfiguracoes);
        
        // Criar array de telas com suas ordens
        const telasComOrdem = telasVinculadas
            .filter(tela => tela.id != null && telaIds.includes(tela.id))
            .map(tela => {
                const config = configs.get(tela.id!);
                const ordem = config?.ordem || 9999; // Telas sem ordem vão para o final
                return { tela, ordem };
            });
        
        // Ordenar por ordem (1, 2, 3, ...)
        telasComOrdem.sort((a, b) => a.ordem - b.ordem);
        
        return telasComOrdem.map(item => item.tela);
    }, [telasVinculadas, telaIds, televisao?.dsConfiguracoes]);

    // Validação de segurança (após hooks)
    if (!televisao || !televisao.nmTelevisao) {
        console.warn('TelevisaoCard: televisão inválida', televisao);
        return null;
    }

    const handleView = () => {
        if (televisao.cdTelevisao != null) {
            navigate(`/display/televisao/${televisao.cdTelevisao}`);
        }
    };

    return (
        <div className="bg-white rounded-lg shadow-md p-6 border-2 border-gray-200 h-full flex flex-col">
            {/* Header */}
            <div className="flex justify-between items-start mb-4">
                <div className="flex-1 min-w-0 pr-2">
                    <h3 className="text-lg font-semibold text-gray-800 truncate">{televisao.nmTelevisao || 'Sem nome'}</h3>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                    <button
                        onClick={() => onEdit(televisao)}
                        className="text-blue-500 hover:text-blue-600"
                        title="Editar televisão"
                    >
                        <Edit3 size={18} />
                    </button>
                    <button
                        onClick={() => televisao.cdTelevisao != null && onDelete(televisao.cdTelevisao)}
                        className="text-red-500 hover:text-red-600"
                        title="Excluir televisão"
                        disabled={televisao.cdTelevisao == null}
                    >
                        <Trash2 size={18} />
                    </button>
                </div>
            </div>

            {/* Content */}
            <div className="space-y-2 flex-1">
                <div className="flex items-center text-sm text-gray-600">
                    <span className="font-bold mr-2">ID:</span>
                    <span className="font-bold">{televisao.cdTelevisao ?? 'N/A'}</span>
                </div>
                <div className="flex items-center text-sm text-gray-600 gap-2 flex-wrap">
                    <span className="font-medium mr-2">Telas Vinculadas:</span>
                    {quantidadeTelas > 0 ? (
                        <span className="font-semibold text-blue-600">
                            {quantidadeTelas} {quantidadeTelas === 1 ? 'tela' : 'telas'}
                        </span>
                    ) : (
                        <span className="text-gray-400 italic">Nenhuma tela vinculada</span>
                    )}
                </div>
                {quantidadeTelas > 0 && (
                    <div className="mt-2">
                        <div className="grid grid-cols-2 gap-1">
                            {telasOrdenadas.map((tela, index) => {
                                const configs = televisaoService.extrairTodasConfigs(televisao.dsConfiguracoes);
                                const config = configs.get(tela.id!);
                                const ordem = config?.ordem || (index + 1);
                                
                                return (
                                    <span
                                        key={tela.id!}
                                        className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium truncate"
                                        title={`Ordem: ${ordem} - ${tela.nome || `ID: ${tela.id}`}`}
                                    >
                                        {ordem}. {tela.nome || `ID: ${tela.id}`}
                                    </span>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>

            {/* Actions */}
            <div className="mt-4 pt-2 space-y-2">
                {quantidadeTelas > 0 && (
                    <button
                        onClick={handleView}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-colors bg-blue-500 text-white hover:bg-blue-600"
                    >
                        <Monitor size={18} />
                        Visualizar
                    </button>
                )}
                <button
                    onClick={() => onLinkScreens(televisao)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-colors bg-green-500 text-white hover:bg-green-600"
                >
                    <Link2 size={18} />
                    {quantidadeTelas > 0 ? 'Gerenciar Telas' : 'Vincular Telas'}
                </button>
            </div>
        </div>
    );
});

TelevisaoCard.displayName = 'TelevisaoCard';
